import type { DirectiveBinding, ObjectDirective } from 'vue'

type ResizeColumnOptions = {
  minWidth?: number
  maxWidth?: number
  handleWidth?: number
  disabled?: boolean
}

type ResolvedOptions = {
  minWidth: number
  maxWidth: number
  handleWidth: number
  disabled: boolean
}

type ResizeState = {
  handle: HTMLDivElement
  onPointerDown: (event: PointerEvent) => void
  onClick: (event: MouseEvent) => void
  options: ResolvedOptions
}

const DEFAULT_MIN_WIDTH = 80
const DEFAULT_HANDLE_WIDTH = 8

const stateMap = new WeakMap<HTMLElement, ResizeState>()

function resolveOptions(
  binding: DirectiveBinding<ResizeColumnOptions | boolean | undefined>,
): ResolvedOptions {
  if (binding.value === false) {
    return {
      minWidth: DEFAULT_MIN_WIDTH,
      maxWidth: Number.POSITIVE_INFINITY,
      handleWidth: DEFAULT_HANDLE_WIDTH,
      disabled: true,
    }
  }

  const raw = (binding.value ?? {}) as ResizeColumnOptions
  return {
    minWidth: raw.minWidth ?? DEFAULT_MIN_WIDTH,
    maxWidth: raw.maxWidth ?? Number.POSITIVE_INFINITY,
    handleWidth: raw.handleWidth ?? DEFAULT_HANDLE_WIDTH,
    disabled: raw.disabled ?? false,
  }
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max)
}

function getHeaderCells(table: HTMLTableElement) {
  const headerRow =
    table.querySelector('thead tr:last-child') ?? table.querySelector('tr')
  if (!headerRow) return [] as HTMLTableCellElement[]
  return Array.from(headerRow.children).filter(
    (child): child is HTMLTableCellElement =>
      child instanceof HTMLTableCellElement &&
      (child.tagName === 'TH' || child.tagName === 'TD'),
  )
}

function ensureTableSetup(table: HTMLTableElement) {
  if (table.dataset.colResizeInitialized === 'true') return
  table.dataset.colResizeInitialized = 'true'
  table.style.width = 'max-content'
  table.style.minWidth = '100%'
}

function ensureColGroup(table: HTMLTableElement): HTMLTableColElement {
  let colGroup = table.querySelector(
    'colgroup[data-col-resize]',
  ) as HTMLTableColElement | null

  if (!colGroup) {
    colGroup = table.querySelector('colgroup') as HTMLTableColElement | null
  }

  if (!colGroup) {
    colGroup = document.createElement('colgroup')
    table.insertBefore(colGroup, table.firstChild)
  }

  colGroup.dataset.colResize = 'true'
  const ensuredColGroup = colGroup

  const headerCells = getHeaderCells(table)
  if (headerCells.length) {
    ensuredColGroup.replaceChildren()
    headerCells.forEach(cell => {
      const col = document.createElement('col')
      const widthPx = cell.dataset.resizedWidth || cell.style.width
      if (widthPx) col.style.width = widthPx
      ensuredColGroup.appendChild(col)
    })
  }

  return ensuredColGroup
}

function getColumnIndex(cell: HTMLTableCellElement, table: HTMLTableElement) {
  const headerCells = getHeaderCells(table)
  if (headerCells.length) {
    const index = headerCells.indexOf(cell)
    if (index !== -1) return index
  }
  if (typeof cell.cellIndex === 'number') return cell.cellIndex
  const parent = cell.parentElement
  if (!parent) return -1
  return Array.from(parent.children).indexOf(cell)
}

function updateHandleVisibility(cell: HTMLTableCellElement, handle: HTMLDivElement) {
  const table = cell.closest('table') as HTMLTableElement | null
  if (!table) return
  const cells = getHeaderCells(table)
  if (!cells.length) return
  if (!cells.includes(cell)) {
    handle.style.display = 'none'
    return
  }
  const index = cells.indexOf(cell)
  handle.style.display = index === cells.length - 1 ? 'none' : 'block'
}

function mountResize(
  el: HTMLElement,
  binding: DirectiveBinding<ResizeColumnOptions | boolean | undefined>,
  options = resolveOptions(binding),
) {
  if (options.disabled) return

  const cell = el as HTMLTableCellElement
  if (stateMap.has(cell)) return
  const table = cell.closest('table') as HTMLTableElement | null
  if (!table) return

  ensureTableSetup(table)

  const handle = document.createElement('div')
  handle.className = 'col-resize-handle'
  handle.style.width = `${options.handleWidth}px`
  handle.style.position = 'absolute'
  handle.style.top = '0'
  handle.style.bottom = '0'
  handle.style.userSelect = 'none'
  handle.style.touchAction = 'none'
  handle.style.zIndex = '2'

  const direction = getComputedStyle(table).direction
  if (direction === 'rtl') {
    handle.style.left = '0'
  } else {
    handle.style.right = '0'
  }

  const computed = getComputedStyle(cell)
  if (computed.position === 'static') {
    cell.style.position = 'relative'
  }

  const onPointerDown = (event: PointerEvent) => {
    const activeState = stateMap.get(cell)
    const activeOptions = activeState?.options ?? options
    if (activeOptions.disabled) return
    if (event.button !== 0 && event.pointerType === 'mouse') return
    event.preventDefault()
    event.stopPropagation()

    const currentTable = cell.closest('table') as HTMLTableElement | null
    if (!currentTable) return

    ensureTableSetup(currentTable)
    const colGroup = ensureColGroup(currentTable)
    const index = getColumnIndex(cell, currentTable)
    if (index < 0 || index >= colGroup.children.length) return

    const col = colGroup.children[index] as HTMLTableColElement
    const startX = event.clientX
    const startWidth = cell.getBoundingClientRect().width
    const isRtl = getComputedStyle(currentTable).direction === 'rtl'

    const bodyStyle = document.body.style
    const previousCursor = bodyStyle.cursor
    const previousUserSelect = bodyStyle.userSelect
    bodyStyle.cursor = 'col-resize'
    bodyStyle.userSelect = 'none'

    handle.classList.add('is-active')

    const onPointerMove = (moveEvent: PointerEvent) => {
      const latestOptions = stateMap.get(cell)?.options ?? activeOptions
      const delta = moveEvent.clientX - startX
      const nextWidth = clamp(
        startWidth + (isRtl ? -delta : delta),
        latestOptions.minWidth,
        latestOptions.maxWidth,
      )

      const widthPx = `${Math.round(nextWidth)}px`
      col.style.width = widthPx
      cell.style.width = widthPx
      cell.dataset.resizedWidth = widthPx
    }

    const onPointerUp = () => {
      window.removeEventListener('pointermove', onPointerMove)
      window.removeEventListener('pointerup', onPointerUp)
      bodyStyle.cursor = previousCursor
      bodyStyle.userSelect = previousUserSelect
      handle.classList.remove('is-active')
    }

    window.addEventListener('pointermove', onPointerMove)
    window.addEventListener('pointerup', onPointerUp)
  }

  const onClick = (event: MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
  }

  handle.addEventListener('pointerdown', onPointerDown)
  handle.addEventListener('click', onClick)
  cell.appendChild(handle)
  updateHandleVisibility(cell, handle)

  const colGroup = ensureColGroup(table)
  const index = getColumnIndex(cell, table)
  if (index >= 0 && index < colGroup.children.length && cell.dataset.resizedWidth) {
    const widthPx = cell.dataset.resizedWidth
    ;(colGroup.children[index] as HTMLTableColElement).style.width = widthPx
  }

  stateMap.set(cell, { handle, onPointerDown, onClick, options })
}

const colResize: ObjectDirective<HTMLElement, ResizeColumnOptions | boolean> = {
  mounted(el, binding) {
    mountResize(el, binding)
  },
  updated(el, binding) {
    const state = stateMap.get(el)
    const nextOptions = resolveOptions(binding)

    if (nextOptions.disabled) {
      if (state) {
        state.handle.removeEventListener('pointerdown', state.onPointerDown)
        state.handle.removeEventListener('click', state.onClick)
        state.handle.remove()
        stateMap.delete(el)
      }
      return
    }

    if (!state) {
      mountResize(el, binding, nextOptions)
      return
    }

    updateHandleVisibility(el as HTMLTableCellElement, state.handle)
    state.options = nextOptions
    state.handle.style.width = `${nextOptions.handleWidth}px`
  },
  beforeUnmount(el) {
    const state = stateMap.get(el)
    if (!state) return
    state.handle.removeEventListener('pointerdown', state.onPointerDown)
    state.handle.removeEventListener('click', state.onClick)
    state.handle.remove()
    stateMap.delete(el)
  },
}

export default colResize
